/**
 * VoiceCommandModal — Comando vocale "intent-aware" per il cantiere immobile.
 *
 * iter v29 r085 · Voice command center
 *
 * Flusso:
 *   1. L'utente registra (riusa VoiceRecorder underlying che già esiste).
 *   2. Trascriviamo (POST /voice/transcribe-only).
 *   3. Classifichiamo intent (POST /voice/property-command).
 *   4. Auto-esecuzione SENZA conferma:
 *      • update_fields → PATCH /properties/:id
 *      • enhance_photos → POST /properties/:id/photos/enhance-batch
 *      • stage_room → POST /photos/:id/stage
 *      • delete_photo → DELETE /photos/:id
 *      • set_cover → PATCH /photos/:id { is_cover: true }
 *      • generate_post → nav ?tab=social&action=post
 *      • generate_reel → nav ?tab=social&action=reel
 *      • generate_brochure → nav ?tab=brochure&action=download
 *      • publish → nav ?tab=publish
 *
 * Toast di conferma con il `human_reply` dell'AI.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Microphone, X, Stop } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const RECORD_MAX_MS = 60 * 1000;  // 60s di sicurezza
const MIME_PREF = "audio/webm;codecs=opus";

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  const cand = [MIME_PREF, "audio/webm", "audio/mp4", "audio/ogg"];
  return cand.find((m) => MediaRecorder.isTypeSupported(m)) || "";
}

const FIELD_LABELS = {
  title: "titolo", listing_type: "tipo", property_type: "tipologia",
  price: "prezzo", surface: "m²", rooms: "locali", bathrooms: "bagni",
  floor: "piano", address: "indirizzo", city: "città",
  features: "caratteristiche", notes: "descrizione",
};

const NUMERIC = new Set(["price", "surface", "rooms", "bathrooms"]);
const LIST = new Set(["features"]);
function normField(k, v) {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  if (!s) return null;
  if (NUMERIC.has(k)) {
    const n = Number(s.replace(/[^0-9.,-]/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  if (LIST.has(k)) return s.split(",").map((x) => x.trim()).filter(Boolean);
  return s;
}

export default function VoiceCommandModal({ open, onClose, property, photos = [], onUpdated }) {
  const nav = useNavigate();
  const [phase, setPhase] = useState("idle");
  // idle → recording → transcribing → classifying → executing → done
  const [transcript, setTranscript] = useState("");
  const [intent, setIntent] = useState(null);
  const [reply, setReply] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);

  // Reset quando si chiude
  useEffect(() => {
    if (!open) {
      setPhase("idle"); setTranscript(""); setIntent(null); setReply("");
      cleanupRecorder();
    }
  }, [open]);

  const cleanupRecorder = () => {
    try { if (stopTimerRef.current) clearTimeout(stopTimerRef.current); } catch { /* */ }
    stopTimerRef.current = null;
    try { recorderRef.current?.stop(); } catch { /* */ }
    recorderRef.current = null;
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    streamRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime() || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = onRecordStop;
      rec.start(250);
      setPhase("recording");
      stopTimerRef.current = setTimeout(() => stopRecording(), RECORD_MAX_MS);
    } catch (e) {
      console.warn("mic unavailable, falling back to text input", e?.message);
      // iter v29 r086 — Fallback testuale quando il microfono non è
      // disponibile (Safari senza permission, dispositivi senza mic, ecc).
      // Non chiudiamo il modal — passiamo a phase="text-input" così
      // l'agente può comunque dare un comando.
      setPhase("text-input");
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch { /* */ }
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
  };

  const onRecordStop = async () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* */ }
    streamRef.current = null;
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    chunksRef.current = [];
    if (blob.size < 800) {
      toast.error("Audio troppo breve. Riprova.");
      setPhase("idle");
      return;
    }
    await processAudio(blob);
  };

  const processAudio = async (blob) => {
    setPhase("transcribing");
    try {
      // 1. Trascrizione
      const fd = new FormData();
      fd.append("file", blob, "command.webm");
      const { data: tr } = await api.post("/voice/transcribe-only", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const tText = (tr?.transcript || "").trim();
      setTranscript(tText);
      if (!tText) {
        toast.error("Non ho sentito niente. Riprova.");
        setPhase("idle");
        return;
      }
      await processTranscript(tText);
    } catch (err) {
      console.error("voice-command transcribe failed", err);
      toast.error(err?.response?.data?.detail || "Non sono riuscito a capire. Riprova.");
      setPhase("idle");
    }
  };

  /** Invio del comando testuale (fallback se mic non disponibile). */
  const submitText = async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    setTranscript(t);
    await processTranscript(t);
  };

  /** Pipeline comune: classify intent + execute (chiamata sia da audio che da text). */
  const processTranscript = async (tText) => {
    try {
      setPhase("classifying");
      const existing = {
        title: property?.title, listing_type: property?.listing_type,
        property_type: property?.property_type, price: property?.price,
        surface: property?.surface, rooms: property?.rooms,
        bathrooms: property?.bathrooms, floor: property?.floor,
        address: property?.address, city: property?.city,
      };
      const { data: cmd } = await api.post("/voice/property-command", {
        transcript: tText,
        property_id: property?.id,
        photo_count: Array.isArray(photos) ? photos.length : 0,
        existing_fields: existing,
      });
      setIntent(cmd?.intent || "unknown");
      setReply(cmd?.human_reply || "");

      setPhase("executing");
      await executeIntent(cmd);
    } catch (err) {
      console.error("voice-command classify failed", err);
      toast.error(err?.response?.data?.detail || "Non ho capito. Riprova.");
      setPhase("idle");
    }
  };

  /** Esegue l'intent classificato. Niente conferma — modalità auto-run. */
  const executeIntent = async (cmd) => {
    const i = cmd?.intent || "unknown";
    const p = cmd?.params || {};
    const f = cmd?.fields || {};
    const pid = property?.id;

    try {
      if (i === "update_fields") {
        // Patch dei campi
        const patch = {};
        const applied = [];
        Object.entries(f).forEach(([k, v]) => {
          if (!(k in FIELD_LABELS)) return;
          const norm = normField(k, v);
          if (norm === null) return;
          patch[k] = norm; applied.push(k);
        });
        if (applied.length === 0) {
          toast.info("Non ho trovato campi da aggiornare nel comando");
        } else {
          await api.put(`/properties/${pid}`, patch);
          const labels = applied.map((k) => FIELD_LABELS[k] || k).join(", ");
          toast.success(`Aggiornati ${applied.length} ${applied.length === 1 ? "campo" : "campi"}`, {
            description: labels.slice(0, 100),
          });
          onUpdated?.();
        }
      } else if (i === "enhance_photos") {
        const scope = p?.scope || "all";
        let ids = [];
        if (scope === "all") ids = photos.map((ph) => ph.id);
        else if (scope === "last" && photos.length) ids = [photos[photos.length - 1].id];
        else if (scope === "some" && Array.isArray(p.indexes)) {
          ids = p.indexes.map((n) => photos[Number(n) - 1]?.id).filter(Boolean);
        }
        if (!ids.length) {
          toast.info("Nessuna foto da migliorare.");
        } else {
          toast.success(cmd.human_reply || `Sto migliorando ${ids.length} foto`, {
            description: "Ci vogliono circa 8 secondi a foto.",
          });
          // Fire-and-forget batch (il server processa in background)
          await api.post(`/properties/${pid}/photos/enhance-batch`, { photo_ids: ids })
            .catch((e) => console.warn("enhance-batch err", e?.message));
          onUpdated?.();
        }
      } else if (i === "stage_room") {
        const idx = Number(p?.photo_index || 1);
        const photo = photos[idx - 1];
        if (!photo) {
          toast.info(`Non c'è una ${idx}ª foto.`);
        } else {
          toast.success(cmd.human_reply || `Sto arredando la ${idx}ª foto`);
          await api.post(`/photos/${photo.id}/stage`, { style: p?.style || "moderno" })
            .catch((e) => console.warn("stage err", e?.message));
          onUpdated?.();
        }
      } else if (i === "restyle_room") {
        const idx = Number(p?.photo_index || 1);
        const photo = photos[idx - 1];
        if (!photo) {
          toast.info(`Non c'è una ${idx}ª foto.`);
        } else {
          toast.success(cmd.human_reply || `Cambio arredo ${idx}ª foto`);
          await api.post(`/photos/${photo.id}/restyle`, { style: p?.style || "moderno" })
            .catch((e) => console.warn("restyle err", e?.message));
          onUpdated?.();
        }
      } else if (i === "delete_photo") {
        const idx = Number(p?.photo_index || 0);
        const photo = photos[idx - 1];
        if (!photo) {
          toast.info(`Non c'è una ${idx}ª foto.`);
        } else {
          await api.delete(`/photos/${photo.id}`);
          toast.success(`Foto ${idx} eliminata`);
          onUpdated?.();
        }
      } else if (i === "set_cover") {
        // Se è stato passato un hint testuale, prova a matchare per file/etichetta
        let photo = null;
        if (p?.photo_index) photo = photos[Number(p.photo_index) - 1];
        if (!photo && p?.hint) {
          const hint = String(p.hint).toLowerCase();
          photo = photos.find((ph) => (
            (ph.label || "").toLowerCase().includes(hint) ||
            (ph.room || "").toLowerCase().includes(hint) ||
            (ph.filename || "").toLowerCase().includes(hint)
          )) || photos[0];
        }
        if (!photo) {
          toast.info("Non trovo la foto giusta.");
        } else {
          await api.patch(`/photos/${photo.id}`, { is_cover: true });
          toast.success(cmd.human_reply || "Copertina cambiata");
          onUpdated?.();
        }
      } else if (i === "generate_post") {
        const plat = p?.platform || "instagram";
        toast.success(cmd.human_reply || "Apro il generatore di post");
        nav(`/dashboard/property/${pid}?tab=social&action=post&platform=${plat}`);
      } else if (i === "generate_reel") {
        const kind = p?.kind || "any";
        toast.success(cmd.human_reply || "Apro il generatore di reel");
        nav(`/dashboard/property/${pid}?tab=social&action=reel&kind=${kind}`);
      } else if (i === "generate_brochure") {
        toast.success(cmd.human_reply || "Apro la pianta arredata");
        nav(`/dashboard/property/${pid}?tab=brochure&action=download`);
      } else if (i === "publish") {
        toast.success(cmd.human_reply || "Vado alla pubblicazione");
        nav(`/dashboard/property/${pid}?tab=publish`);
      } else {
        toast.info("Non ho capito · prova: 'migliora le foto', 'fammi un post', 'scrivi annuncio', 'pubblica online'");
      }
    } catch (err) {
      console.error("execute intent failed", err);
      toast.error(err?.response?.data?.detail || "Non riesco a completare l'operazione.");
    } finally {
      setPhase("done");
      // Chiusura automatica veloce così non disturba
      setTimeout(() => onClose?.(), 1100);
    }
  };

  // Auto-start su open
  useEffect(() => {
    if (open && phase === "idle") {
      startRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isWorking = phase === "transcribing" || phase === "classifying" || phase === "executing";

  // Local state per input testuale (fallback)
  const [textInput, setTextInput] = useState("");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-[420px] bg-[#FAF8F4] border-none rounded-3xl p-0 overflow-hidden" data-testid="voice-cmd-modal">
        <DialogTitle className="sr-only">Comando vocale annuncio</DialogTitle>
        <button
          type="button"
          onClick={() => onClose?.()}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[#1C1C1A]/8 hover:bg-[#1C1C1A]/14 flex items-center justify-center z-10"
          data-testid="voice-cmd-close"
        >
          <X size={14} weight="bold" className="text-[#1C1C1A]" />
        </button>

        <div className="px-6 pt-10 pb-8 text-center">
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#7C8B6E] mb-2">
            {phase === "recording" ? "Sto ascoltando" :
             phase === "text-input" ? "Scrivi il comando" :
             phase === "idle" ? "Comando vocale" :
             isWorking ? "Un attimo…" : "Fatto"}
          </div>
          <h2 className="font-editorial text-[26px] leading-tight text-[#1C1C1A]" data-testid="voice-cmd-title">
            {phase === "recording" ? (
              <>Parla pure.<br /><span className="italic-accent text-[#7C8B6E]">Cosa devo fare?</span></>
            ) : phase === "text-input" ? (
              <>Microfono non attivo.<br /><span className="italic-accent text-[#7C8B6E]">Scrivilo qui.</span></>
            ) : phase === "idle" ? (
              <>Attivo<br />il microfono…</>
            ) : isWorking ? (
              <>{reply || "Procedo…"}</>
            ) : (
              <>Fatto.</>
            )}
          </h2>

          {transcript && (
            <p className="mt-4 text-[13px] text-[#6B6A65] leading-relaxed italic" data-testid="voice-cmd-transcript">
              "{transcript}"
            </p>
          )}

          {/* TEXT INPUT mode · fallback se mic non disponibile */}
          {phase === "text-input" && (
            <form
              onSubmit={(e) => { e.preventDefault(); submitText(textInput); }}
              className="mt-6 space-y-3"
              data-testid="voice-cmd-text-form"
            >
              <textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                rows={3}
                autoFocus
                placeholder='Es: "migliora tutte le foto" · "il prezzo è 580 mila" · "fammi un post"'
                className="w-full rounded-xl border border-[#E5E7E0] bg-white px-3 py-2.5 text-[14px] text-[#1C1C1A] placeholder-[#9A968B] focus:outline-none focus:border-[#7C8B6E] resize-none"
                data-testid="voice-cmd-text-input"
              />
              <button
                type="submit"
                disabled={!textInput.trim()}
                className="w-full rounded-full bg-[#1C1C1A] hover:bg-black disabled:opacity-40 text-white py-2.5 text-[13.5px] font-semibold transition-colors"
                data-testid="voice-cmd-text-submit"
              >
                Invia comando
              </button>
            </form>
          )}

          {/* Cerchio mic / spinner */}
          {phase !== "text-input" && (
            <div className="mt-7 mb-5 flex items-center justify-center">
              {phase === "recording" ? (
                <button
                  type="button"
                  onClick={stopRecording}
                  className="w-24 h-24 rounded-full bg-[#1C1C1A] hover:bg-black flex items-center justify-center ring-8 ring-[#1C1C1A]/8 transition-all"
                  data-testid="voice-cmd-stop"
                  aria-label="Ferma registrazione"
                >
                  <Stop size={36} weight="fill" className="text-white" />
                </button>
              ) : isWorking ? (
                <div className="w-24 h-24 rounded-full bg-[#7C8B6E]/14 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full border-4 border-[#7C8B6E]/30 border-t-[#7C8B6E] animate-spin" />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#7C8B6E]/14 flex items-center justify-center">
                  <Microphone size={36} weight="fill" className="text-[#7C8B6E]" />
                </div>
              )}
            </div>
          )}

          {phase === "recording" && (
            <div className="text-[11px] text-[#7C8B6E] font-medium tracking-wide">
              Tocca quando hai finito · max 1 minuto
            </div>
          )}
        </div>

        {/* Hint sotto · esempi di comandi */}
        {(phase === "idle" || phase === "recording" || phase === "text-input") && (
          <div className="border-t border-[#1C1C1A]/06 bg-white px-6 py-4">
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#9A968B] mb-2">
              Cosa puoi chiedere
            </div>
            <ul className="text-[12.5px] text-[#3F3D38] space-y-1 leading-relaxed">
              <li>· "Migliora tutte le foto"</li>
              <li>· "Il prezzo è 620 mila e i bagni sono 2"</li>
              <li>· "Fammi un post per Instagram"</li>
              <li>· "Genera i post social"</li>
              <li>· "Pubblica online"</li>
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
